import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar, ChevronLeft, ChevronRight, Building2, Info } from "lucide-react";
import { listSitesLite, listBookingsForGrid, listBlackoutsForGrid, listBusySlots, getAvailabilityInputs } from "@/lib/api";
import { useViewAsClient } from "@/lib/viewAsClient";
import { useAuth } from "@/lib/auth";
import { composeInSiteTz, siteTz, siteTimeBadge, placeTimeLabel } from "@/lib/datetime";
import SiteClockLine from "@/components/SiteClockLine";
import { useLocation } from "react-router-dom";
import { bayRules, rangeState, siteDay, dayCells, DEAD_STATUSES, STATE_STYLE } from "@/lib/availability";

/**
 * Step 1 of the new-booking funnel.
 * Site filter → bay grid (site-local day) → click an available cell.
 * Cell states come from lib/availability (hours, open days, blackouts,
 * bookings + buffer, lead time, horizon). Admins may override with a confirm.
 */
const HOURS = Array.from({ length: 24 }, (_, h) => h);

export default function NewBookingPicker() {
  const navigate = useNavigate();
  const location = useLocation();
  const { viewAsClientId } = useViewAsClient();
  const { hasRole } = useAuth();
  const isAdmin = hasRole(["super_admin", "client_admin", "site_admin", "dock_manager"]);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [inputs, setInputs] = useState({ site: null, bays: [], bufferEnabled: false, autoAssign: false });
  const [bookings, setBookings] = useState([]);
  const [blackouts, setBlackouts] = useState([]);
  const [dayOffset, setDayOffset] = useState(0); // 0 = today, 1 = tomorrow, ...
  const [loading, setLoading] = useState(true);

  // Stash referrer so /bookings/new/edit can navigate back after save.
  // The AppLayout NavLink already does this when the user clicks "New booking",
  // but direct links / back-forward navigation skip that path, so we re-stash
  // here on mount if nothing's been set.
  useEffect(() => {
    try {
      const existing = sessionStorage.getItem("lbs.bookingReturnTo");
      if (!existing) {
        const state = location.state || {};
        const from = state.from && !String(state.from).startsWith("/bookings/new")
          ? state.from
          : "";
        if (from) sessionStorage.setItem("lbs.bookingReturnTo", from);
      }
    } catch (_e) { /* sessionStorage disabled */ }
  }, [location.state]);

  // Load sites whenever the super-admin's "View as client" picker changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await listSitesLite(viewAsClientId || null);
      if (cancelled) return;
      const list = data || [];
      setSites(list);
      setSiteId(list[0]?.id || "");
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [viewAsClientId]);

  // Site + bay rules for the availability engine.
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    getAvailabilityInputs(siteId, { includeInactive: isAdmin }).then((r) => { if (!cancelled) setInputs(r); });
    return () => { cancelled = true; };
  }, [siteId, isAdmin]);

  const site = sites.find((s) => s.id === siteId);
  const tz = siteTz(site);
  const day = useMemo(() => siteDay(tz, dayOffset), [tz, dayOffset]);
  const dayStartIso = composeInSiteTz(day, tz);
  const dayEndIso = composeInSiteTz({ ...siteDay(tz, dayOffset + 1) }, tz);
  const bays = useMemo(() => (inputs.site?.id === siteId ? inputs.bays : []), [inputs, siteId]);
  const autoAssign = inputs.site?.id === siteId && inputs.autoAssign;

  // Bookings + blackouts for one site-local day (± buffer margin).
  const loadDay = useCallback(async (offset) => {
    const startIso = composeInSiteTz(siteDay(tz, offset), tz);
    const endIso = composeInSiteTz(siteDay(tz, offset + 1), tz);
    const from = new Date(new Date(startIso).getTime() - 3 * 3600000).toISOString();
    const [b, x, busy] = await Promise.all([
      listBookingsForGrid(siteId, from, endIso),
      listBlackoutsForGrid(siteId, startIso, endIso),
      listBusySlots(siteId, from, endIso),
    ]);
    // Bookers can only read their own rows — add anonymous "Booked" blocks for the rest.
    const seen = new Set((b.data || []).map((r) => r.id));
    const others = (busy.data || []).filter((r) => !seen.has(r.id)).map((r) => ({ ...r, title: "Booked" }));
    const all = [...(b.data || []), ...others];
    return { bookings: all.filter((r) => !DEAD_STATUSES.includes(r.status)), blackouts: x.data || [] };
  }, [siteId, tz]);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    loadDay(dayOffset).then((d) => {
      if (cancelled) return;
      setBookings(d.bookings);
      setBlackouts(d.blackouts);
    });
    return () => { cancelled = true; };
  }, [siteId, dayOffset, loadDay]);

  const step = Math.min(30, ...bays.map((b) => b.slot_duration_minutes || inputs.site?.slot_duration_minutes || 30));
  const cells = useMemo(() => dayCells(day, tz, step), [day, tz, step]);

  const ctxFor = useCallback((bay, bk, bo) => ({
    rules: bayRules(inputs.site, bay, { bufferEnabled: inputs.bufferEnabled }),
    data: {
      bookings: bk.filter((b) => b.bay_id === bay.id),
      blackouts: bo.filter((x) => !x.bay_id || x.bay_id === bay.id),
    },
  }), [inputs]);
  const bayCtx = useCallback((bay) => ctxFor(bay, bookings, blackouts), [ctxFor, bookings, blackouts]);

  // Auto-assign: first active bay (in bay order) that can take a booking starting at ms.
  const firstFreeBay = useCallback((ms, bk = bookings, bo = blackouts) => bays.find((bay) => {
    if (!bay.is_active) return false;
    const { rules, data } = ctxFor(bay, bk, bo);
    const mins = Math.round((ms - new Date(composeInSiteTz(siteDay(tz, 0), tz)).getTime()) / 60000);
    if (((mins % rules.slot) + rules.slot) % rules.slot !== 0) return false;
    return rangeState(ms, ms + rules.minLen * 60000, rules, data) === "open";
  }), [bays, bookings, blackouts, ctxFor, tz]);

  const [highlight, setHighlight] = useState(null);
  const [searching, setSearching] = useState(false);
  const findNext = useCallback(async ({ auto = false } = {}) => {
    setSearching(true);
    try {
      const maxDays = Math.max(...bays.map((b) => bayRules(inputs.site, b).horizonD ?? 60), 1);
      for (let off = 0; off <= Math.min(maxDays, 90); off++) {
        const d = off === dayOffset && !auto ? { bookings, blackouts } : await loadDay(off);
        const hit = dayCells(siteDay(tz, off), tz, step).find((ms) => firstFreeBay(ms, d.bookings, d.blackouts));
        if (hit) {
          setDayOffset(off);
          setHighlight(hit);
          if (auto) {
            if (off > 0) toast.info(`No free slots today — showing ${new Intl.DateTimeFormat("en-AU", { timeZone: tz, weekday: "short", day: "numeric", month: "short" }).format(hit)}, the next day with a free slot`);
            return;
          }
          toast.success(`Next available: ${new Intl.DateTimeFormat("en-AU", { timeZone: tz, weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).format(hit)}${autoAssign ? "" : " — pick a bay"}`);
          return;
        }
      }
      if (!auto) toast.error("No available times found in the booking window.");
    } finally {
      setSearching(false);
    }
  }, [bays, inputs.site, dayOffset, bookings, blackouts, loadDay, tz, step, firstFreeBay, autoAssign]);

  // Open on the next day that has a free slot (once per site), so an empty "today" never looks dead.
  const autoJumped = useRef("");
  useEffect(() => {
    if (!siteId || inputs.site?.id !== siteId || !bays.length || autoJumped.current === siteId) return;
    autoJumped.current = siteId;
    findNext({ auto: true });
  }, [siteId, inputs.site, bays, findNext]);

  const onPick = useCallback((bay, startMs) => {
    const { rules, data } = bayCtx(bay);
    const endMs = startMs + rules.minLen * 60000;
    const state = rangeState(startMs, endMs, rules, data);
    if (state === "past") return;
    if (state !== "open") {
      const label = STATE_STYLE[state].label.toLowerCase();
      if (!isAdmin) {
        toast.error(`That time isn't available (${label}). Pick a white cell.`);
        return;
      }
      if (!window.confirm(`Override: this ${rules.minLen}-minute slot is ${label}. Book it anyway?`)) return;
    }
    navigate("/bookings/new/docs", {
      state: {
        site: { id: site.id, name: site.name, code: site.code, client_id: site.client_id, timezone: tz },
        bay: { id: bay.id, name: bay.name, code: bay.code, description: bay.description },
        scheduledStart: new Date(startMs).toISOString(),
        scheduledEnd: new Date(endMs).toISOString(),
        clientId: site.client_id,
        timezone: tz,
      },
    });
  }, [bayCtx, isAdmin, navigate, site, tz]);

  const fmtDay = (d) => new Date(Date.UTC(d.year, d.month - 1, d.day)).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });

  return (
    <div className="space-y-4" data-testid="new-booking-picker">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Calendar className="h-6 w-6" /> New booking — pick a slot
          </h1>
          <p className="text-sm text-slate-500">
            Click a white (available) cell on a bay to start. The next step is documents.
            {siteId && (
              <span className="ml-2 inline-flex items-center text-xs text-slate-500 bg-slate-100 rounded px-1.5 py-0.5" data-testid="picker-tz-badge">
                {sites.find((s) => s.id === siteId) ? `@ ${placeTimeLabel(sites.find((s) => s.id === siteId), composeInSiteTz({ ...day, hour: 12 }, tz))}` : siteTimeBadge(siteTz(null))}
              </span>
            )}
          </p>
          {siteId && inputs.site?.id === siteId && (
            <SiteClockLine tz={tz} stepMin={step}
              leadH={bays.length ? Math.min(...bays.map((b) => b.min_lead_time_hours ?? inputs.site.min_lead_time_hours ?? 0)) || null
                                 : inputs.site.min_lead_time_hours || null} />
          )}
        </div>

        <div className="flex items-center gap-2">
          <Label htmlFor="site" className="text-xs uppercase tracking-wide text-slate-500">Site</Label>
          <select
            id="site"
            data-testid="site-filter"
            className="h-9 rounded-md border bg-white px-2 text-sm"
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
          >
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          <Button variant="outline" size="sm" onClick={() => setDayOffset((d) => Math.max(0, d - 1))} disabled={dayOffset === 0} data-testid="day-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm font-medium px-2 min-w-[120px] text-center" data-testid="day-label">{fmtDay(day)}</div>
          <Button variant="outline" size="sm" onClick={() => setDayOffset((d) => d + 1)} data-testid="day-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button size="sm" onClick={findNext} disabled={searching || !bays.length} data-testid="next-available-btn">
            {searching ? "Searching…" : "Next available"}
          </Button>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="py-10 text-center text-slate-500">Loading…</CardContent></Card>
      ) : !siteId ? (
        <Card><CardContent className="py-10 text-center text-slate-500">No sites available.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <div className="min-w-[1100px]">
              <div className="grid border-b" style={{ gridTemplateColumns: `200px repeat(${1440 / step}, minmax(18px, 1fr))` }}>
                <div className="px-3 py-2 text-xs font-semibold text-slate-500 sticky left-0 bg-white border-r">
                  <Building2 className="h-3.5 w-3.5 inline mr-1" /> Bay
                </div>
                {HOURS.map((h) => (
                  <div key={`h-${h}`} style={{ gridColumn: `span ${60 / step}` }} className="px-1 py-2 text-xs text-slate-400 text-center border-l">
                    {String(h).padStart(2, "0")}:00
                  </div>
                ))}
              </div>

              {bays.length === 0 ? (
                <div className="p-6 text-center text-slate-500 text-sm">No bays configured for this site yet.</div>
              ) : [
                autoAssign && <div key="any" className="grid bg-emerald-50/40" style={{ gridTemplateColumns: `200px repeat(${1440 / step}, minmax(18px, 1fr))` }} data-testid="bay-row-any">
                  <div className="px-3 py-3 border-b border-r sticky left-0 bg-emerald-50">
                    <div className="text-sm font-medium text-emerald-800">Any free bay</div>
                    <div className="text-[11px] text-emerald-700/70">auto-assigns the first free bay</div>
                  </div>
                  {cells.map((ms) => {
                    const free = firstFreeBay(ms);
                    const hm = new Intl.DateTimeFormat("en-AU", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).format(ms);
                    return (
                      <button key={`any-${ms}`} type="button" disabled={!free}
                        onClick={() => free && onPick(free, ms)}
                        data-testid={`slot-any-${hm.replace(":", "")}`} data-state={free ? "open" : "none"}
                        title={free ? `${hm} · ${free.name}` : `${hm} · no free bay`}
                        className={`h-10 border-b border-l text-[10px] transition-colors ${free ? STATE_STYLE.open.cls : "bg-slate-100 cursor-not-allowed"} ${highlight === ms ? "ring-2 ring-emerald-500 ring-inset" : ""}`}
                      />
                    );
                  })}
                </div>,
                ...bays.map((bay) => {
                const { rules, data } = bayCtx(bay);
                const span = rules.slot % step === 0 ? rules.slot / step : 1;
                const starts = cells.filter((_, i) => i % span === 0);
                return (
                <div
                  key={bay.id}
                  className="grid hover:bg-slate-50/50"
                  style={{ gridTemplateColumns: `200px repeat(${1440 / step}, minmax(18px, 1fr))` }}
                  data-testid={`bay-row-${bay.id}`}
                >
                  <div className="px-3 py-3 border-b border-r sticky left-0 bg-white">
                    <div className="flex items-center gap-1.5">
                      <div className="text-sm font-medium" style={{ color: bay.color || undefined }}>{bay.name}</div>
                      {!bay.is_active && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600" data-testid={`bay-inactive-badge-${bay.id}`}>
                          inactive
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500">{rules.slot}-min slots · books {rules.minLen} min</div>
                  </div>

                  {starts.map((cellStart) => {
                    const state = rangeState(cellStart, cellStart + span * step * 60000, rules, data);
                    const st = STATE_STYLE[state];
                    const p = new Date(cellStart);
                    const hm = new Intl.DateTimeFormat("en-AU", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).format(p);
                    const blocked = state === "past" || (!isAdmin && state !== "open");
                    return (
                      <button
                        key={`${bay.id}-${cellStart}`}
                        type="button"
                        style={{ gridColumn: `span ${span}` }}
                        data-testid={`slot-${bay.id}-${hm.replace(":", "")}`}
                        data-state={state}
                        disabled={blocked}
                        onClick={() => onPick(bay, cellStart)}
                        className={`h-12 border-b border-l text-[10px] transition-colors ${st.cls} ${blocked ? "cursor-not-allowed" : ""} ${!autoAssign && highlight === cellStart && state === "open" ? "ring-2 ring-emerald-500 ring-inset" : ""}`}
                        title={`${hm} · ${st.label}${isAdmin && state !== "open" && state !== "past" ? " (admin override)" : ""}`}
                      >
                        {state === "booked" ? "•" : ""}
                      </button>
                    );
                  })}
                </div>
                );
              })]}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-600" data-testid="picker-legend">
        {["open", "booked", "buffer", "blackout", "closed", "break", "too_soon"].map((k) => (
          <span key={k} className="inline-flex items-center gap-1">
            <span className={`inline-block h-3 w-4 rounded-sm border ${STATE_STYLE[k].cls}`} /> {STATE_STYLE[k].label}{k === "too_soon" ? " / too far" : ""}
          </span>
        ))}
        {isAdmin && <span className="text-slate-400">Admins can click non-white cells to override.</span>}
      </div>

      <div className="rounded-md border bg-blue-50 px-3 py-2 text-xs text-blue-700 flex items-start gap-2">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          Step 1 of 3 — pick a slot. Step 2 will let you upload PO / Manifest / Invoice (and a DG Manifest if applicable).
          Step 3 opens the full booking form pre-filled.
        </div>
      </div>
    </div>
  );
}
